/**
 * bulkAction/BulkReminderModal.jsx
 *
 * Send email reminders for selected KPI questions.
 *
 * Props: show, onHide, selectedIds, onSuccess
 */
import { useState, useEffect, useRef } from "react";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config.json";
import { S, Spin } from "./Shared";

const BulkReminderModal = ({ show, onHide, selectedIds = [], onSuccess }) => {
  const [emails,  setEmails]  = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState("");
  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  /* reset on open */
  useEffect(() => {
    if (show) {
      setEmails("");
      setMessage("");
      setError("");
      setSuccess("");
    }
  }, [show]);

  const handleSend = async () => {
    setError("");
    const emailList = emails
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);

    if (!emailList.length) {
      setError("Please enter at least one valid email address.");
      return;
    }
    if (!message.trim()) {
      setError("Please enter a reminder message.");
      return;
    }

    setSending(true);
    try {
      const { isSuccess } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}sendReminderEmails`,
        {},
        {
          emailAddresses: emailList,
          message:        message.trim(),
          questionIds:    selectedIds,
        },
        "POST"
      );
      if (isSuccess) {
        setSuccess("✅ Reminder emails sent successfully!");
        onSuccess?.();
        setTimeout(() => onHide(), 1800);
      } else {
        setError("Failed to send reminders. Please try again.");
      }
    } catch {
      setError("Server error occurred.");
    } finally {
      if (isMounted.current) setSending(false);
    }
  };

  if (!show) return null;

  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, maxWidth: 480 }}>
        <div style={S.header}>
          <span style={S.title}>Send Email Reminder</span>
          <button onClick={onHide} disabled={sending} style={S.closeBtn}>
            ✕
          </button>
        </div>

        <div style={S.body}>
          {error   && <div style={S.errBox}>{error}</div>}
          {success && <div style={S.successBox}>{success}</div>}

          <div style={S.infoBox}>
            Sending reminder for{" "}
            <b>{selectedIds.length > 0 ? selectedIds.length : "all"}</b>{" "}
            KPI(s).
          </div>

          {/* Email addresses */}
          <div style={S.field}>
            <div style={S.fieldLabel}>
              Email Addresses
              <span
                style={{
                  fontSize: 10,
                  color: "#94a3b8",
                  textTransform: "none",
                  fontWeight: 400,
                }}
              >
                (comma-separated)
              </span>
            </div>
            <textarea
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder="alice@company.com, bob@company.com"
              rows={3}
              style={S.textarea}
            />
          </div>

          {/* Message */}
          <div style={S.field}>
            <div style={S.fieldLabel}>Reminder Message</div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Please complete the assigned KPI data entry…"
              rows={5}
              style={S.textarea}
            />
          </div>
        </div>

        <div style={S.footer}>
          <button
            onClick={onHide}
            disabled={sending}
            style={S.btnSecondary}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            style={{ ...S.btnPrimary, opacity: sending ? 0.75 : 1 }}
          >
            {sending ? (
              <>
                <Spin light size={12} /> Sending…
              </>
            ) : (
              "Send Reminder"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkReminderModal;